/**
 * CalendarMonthGrid (DESIGN.md T13): six weeks of the month. Multi-day and all-day events are bars across their days
 * (continuing into the next week), timed events show their start time; a day with more events than fit shows
 * "+n more", which opens a popover with all events of that day. Days of other months are dimmed; today is marked.
 *
 * Direct manipulation: click a free part of a day to select it, drag across days to select several (all-day range,
 * e.g. for a multi-day event), double-click to act on the selection; movable events (`canDrag`) can be dragged to
 * another day (time of day and length are kept; Esc cancels).
 */
import { useEffect, useMemo, useRef, useState, type PointerEvent } from 'react';
import { cn } from '../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../components/popover';
import { allDayRange, moveToDay, sameRange } from './drag';
import { EventChip } from './event-chip';
import { usePointerDrag } from './use-pointer-drag';
import { inTimeOrder, rovingKeyDown, tabStopOf } from './roving';
import { isMultiDay, layoutWeek, monthMatrix, overlapsDay, sameDay, startOfDay, type CalendarGridEvent } from './model';
import { SLOT_CONTEXT_ID } from './time-grid';

export interface CalendarMonthGridProps {
  month: Date;
  weekStartsOn: number;
  events: readonly CalendarGridEvent[];
  now: Date;
  selectedId?: string | undefined;
  onSelectEvent: (id: string) => void;
  /** Click on a day number (e.g. open that day). */
  onSelectDay: (day: Date) => void;
  formatWeekday: (day: Date) => string;
  formatDayNumber: (day: Date) => string;
  /** Accessible name of a day button (full date); heading of the "more" popover. */
  formatDayLabel: (day: Date) => string;
  eventLabel: (event: CalendarGridEvent) => string;
  /** "+{n} more". */
  moreLabel: (count: number) => string;
  /** Accessible name of the grid. */
  label: string;
  /** Lanes per week row before "+n more" (default: as many as the row height fits). */
  maxPerDay?: number;
  canDrag?: ((event: CalendarGridEvent) => boolean) | undefined;
  /** An event was dropped on another day (same time of day). */
  onChangeEvent?: ((id: string, start: Date, end: Date) => void) | undefined;
  /** Ctrl/Alt+drag: a copy of the event on the other day. */
  onCopyEvent?: ((id: string, start: Date, end: Date) => void) | undefined;
  /** Click or drag on free space of days selects them (all-day range, end exclusive). */
  onSelectRange?: ((start: Date, end: Date, allDay: boolean) => void) | undefined;
  selection?: { start: Date; end: Date; allDay: boolean } | undefined;
  onOpenSelection?: (() => void) | undefined;
  /** Start time of timed events ("9:00"). */
  formatTime?: ((date: Date) => string) | undefined;
  /** Label of the selection ("3 days"). */
  formatSelection?: ((range: { start: Date; end: Date; allDay: boolean }) => string) | undefined;
  /** Week number label at the start of each row (setting "week numbers"). */
  weekNumberLabel?: ((weekStart: Date) => string) | undefined;
  className?: string;
}

export function CalendarMonthGrid({
  month,
  weekStartsOn,
  events,
  now,
  selectedId,
  onSelectEvent,
  onSelectDay,
  formatWeekday,
  formatDayNumber,
  formatDayLabel,
  eventLabel,
  moreLabel,
  label,
  maxPerDay: fixedLanes,
  canDrag,
  onChangeEvent,
  onCopyEvent,
  onSelectRange,
  selection,
  onOpenSelection,
  formatTime,
  formatSelection,
  weekNumberLabel,
  className,
}: CalendarMonthGridProps) {
  const weeks = useMemo(() => monthMatrix(month, weekStartsOn), [month, weekStartsOn]);
  // Lanes per row follow the height of a week row (about 1.4rem per lane below the day number).
  const body = useRef<HTMLDivElement>(null);
  const [rowHeight, setRowHeight] = useState(0);
  useEffect(() => {
    const element = body.current;
    if (!element || fixedLanes !== undefined) return;
    const measure = () => setRowHeight(element.clientHeight / 6);
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure);
    observer?.observe(element);
    return () => observer?.disconnect();
  }, [fixedLanes]);
  const maxPerDay = fixedLanes ?? (rowHeight > 0 ? Math.max(2, Math.floor((rowHeight - 34) / 22)) : 3);
  const layouts = useMemo(() => weeks.map((week) => layoutWeek(events, week, maxPerDay)), [weeks, events, maxPerDay]);
  const { begin, suppressClick } = usePointerDrag();
  const [drop, setDrop] = useState<{ id: string; day: number }>();
  const [range, setRange] = useState<{ from: Date; to: Date }>();
  const [more, setMore] = useState<number>();
  const draggable = (event: CalendarGridEvent) => Boolean(onChangeEvent && canDrag?.(event));
  const dayAt = (x: number, y: number): Date | undefined => {
    const iso = document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-month-day]')?.dataset.monthDay;
    return iso ? new Date(iso) : undefined;
  };

  const grab = (event: CalendarGridEvent) => (e: PointerEvent<HTMLElement>) => {
    if (!draggable(event) || !onChangeEvent) return;
    // Multi-day bars keep the offset between the grabbed day and their start.
    const grabbed = dayAt(e.clientX, e.clientY) ?? startOfDay(event.start);
    const offset = Math.round((startOfDay(grabbed).getTime() - startOfDay(event.start).getTime()) / 86_400_000);
    let target: Date | undefined;
    const copy = e.ctrlKey || e.metaKey || e.altKey;
    begin(e, {
      move: (x, y) => {
        const day = dayAt(x, y);
        if (!day) return;
        target = new Date(day.getFullYear(), day.getMonth(), day.getDate() - offset);
        setDrop({ id: event.id, day: day.getTime() });
      },
      end: (click) => {
        setDrop(undefined);
        if (click || !target) return;
        const next = moveToDay(event, target);
        if (sameRange(next, event)) return;
        if (copy && onCopyEvent) onCopyEvent(event.id, next.start, next.end);
        else onChangeEvent(event.id, next.start, next.end);
      },
      cancel: () => setDrop(undefined),
    });
  };

  const startSelect = (day: Date) => (e: PointerEvent<HTMLElement>) => {
    if (!onSelectRange || e.target !== e.currentTarget) return;
    // No text selection from dragging over free time (it would take the right-click to the browser menu).
    if (e.pointerType === 'mouse') e.preventDefault();
    let to = day;
    begin(e, {
      move: (x, y) => {
        to = dayAt(x, y) ?? to;
        setRange({ from: day, to });
      },
      end: () => {
        setRange(undefined);
        const next = allDayRange(day, to);
        onSelectRange(next.start, next.end, true);
      },
      cancel: () => setRange(undefined),
    });
  };

  const select = (id: string) => {
    if (suppressClick.current) return;
    setMore(undefined);
    onSelectEvent(id);
  };
  // Roving tab stop over the visible events (the "+n more" popover keeps its own order).
  const ordered = inTimeOrder(layouts.flatMap((l) => l.shown.map((s) => s.event)).filter((e, i, all) => all.findIndex((x) => x.id === e.id) === i));
  const tabStop = tabStopOf(ordered, selectedId);
  const shownRange = range ? allDayRange(range.from, range.to) : selection?.allDay ? selection : selection ? { start: startOfDay(selection.start), end: selection.end } : undefined;
  const inRange = (day: Date) => Boolean(shownRange && day >= startOfDay(shownRange.start) && day < shownRange.end);
  const chip = (event: CalendarGridEvent, extra?: string, inPopover = false) => (
    <EventChip
      event={event}
      compact
      className={cn('h-5 w-full shrink-0 max-sm:px-0.5 max-sm:[&_svg]:hidden', extra)}
      selected={selectedId === event.id}
      onSelect={select}
      label={eventLabel(event)}
      time={!event.allDay && !isMultiDay(event) && formatTime ? formatTime(event.start) : undefined}
      draggable={draggable(event)}
      onGrab={grab(event)}
      faded={drop?.id === event.id}
      contextId={event.id}
      tabIndex={inPopover || tabStop === event.id ? 0 : -1}
    />
  );

  return (
    <section aria-label={label} data-no-native-menu="" onKeyDown={rovingKeyDown(ordered)} className={cn('flex min-h-0 flex-1 flex-col select-none', className)}>
      <div className={cn('grid shrink-0 border-b', weekNumberLabel ? 'grid-cols-[2rem_repeat(7,minmax(0,1fr))]' : 'grid-cols-7')}>
        {weekNumberLabel && <div />}
        {weeks[0]?.map((day) => (
          <div key={day.getDay()} className="py-1.5 text-center text-xs text-muted-foreground">
            {formatWeekday(day)}
          </div>
        ))}
      </div>
      <div ref={body} className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {weeks.map((week, row) => {
          const layout = layouts[row] ?? { shown: [], hidden: [] };
          const lanes = Math.min(maxPerDay, Math.max(0, ...layout.shown.map((s) => s.lane + 1)) + (layout.hidden.some((h) => h.length > 0) ? 1 : 0));
          return (
            <div key={week[0]?.getTime() ?? row} className="flex min-h-24 flex-1 border-b">
              {weekNumberLabel && week[0] && <div className="w-8 shrink-0 border-r pt-1.5 text-center text-xs text-muted-foreground">{weekNumberLabel(week[0])}</div>}
              <div className="relative min-w-0 flex-1">
                {/* Day cells: background, borders, selection, drop target, click/drag to select. */}
                <div className="absolute inset-0 grid grid-cols-7">
                  {week.map((day) => (
                    <div
                      key={day.getTime()}
                      data-month-day={day.toISOString()}
                      data-context-id={SLOT_CONTEXT_ID}
                      onPointerDown={startSelect(day)}
                      onDoubleClick={(e) => e.target === e.currentTarget && onOpenSelection?.()}
                      onContextMenu={(e) => {
                        if (e.target !== e.currentTarget || !onSelectRange || inRange(day)) return;
                        const next = allDayRange(day, day);
                        onSelectRange(next.start, next.end, true);
                      }}
                      className={cn(
                        'border-r',
                        day.getMonth() !== month.getMonth() && 'bg-muted/40',
                        inRange(day) && 'bg-primary/15 ring-1 ring-primary ring-inset',
                        drop?.day === day.getTime() && 'bg-primary/15 ring-2 ring-primary ring-inset',
                      )}
                    />
                  ))}
                </div>
                {/* Content: day numbers, bars and chips in lanes; only the items take pointer events. */}
                <div className="pointer-events-none relative grid grid-cols-7 gap-y-0.5 pb-1" style={{ gridTemplateRows: lanes > 0 ? `auto repeat(${lanes}, 1.25rem)` : 'auto' }}>
                  {week.map((day, col) => {
                    const today = sameDay(day, now);
                    const first = shownRange && col === week.findIndex(inRange) && formatSelection;
                    return (
                      <div key={day.getTime()} className="flex min-w-0 items-center gap-1 p-1" style={{ gridColumn: col + 1, gridRow: 1 }}>
                        <button
                          type="button"
                          onClick={() => onSelectDay(day)}
                          aria-label={formatDayLabel(day)}
                          aria-current={today ? 'date' : undefined}
                          className={cn(
                            'pointer-events-auto flex size-6 shrink-0 items-center justify-center rounded-full text-xs outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring',
                            day.getMonth() !== month.getMonth() && 'text-muted-foreground',
                            today && 'bg-primary font-semibold text-primary-foreground hover:bg-primary',
                          )}
                        >
                          {formatDayNumber(day)}
                        </button>
                        {first && (
                          <span aria-hidden className="truncate text-xs font-medium">
                            {formatSelection({ ...shownRange, allDay: true })}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {layout.shown.map((segment) => (
                    <div key={segment.event.id} className="pointer-events-auto flex min-w-0 px-0.5" style={{ gridColumn: `${segment.from + 1} / ${segment.to + 2}`, gridRow: segment.lane + 2 }}>
                      {chip(segment.event, cn(segment.continuesBefore && 'rounded-l-none border-l-0', segment.continuesAfter && 'rounded-r-none'))}
                    </div>
                  ))}
                  {layout.hidden.map((list, col) => {
                    if (list.length === 0) return null;
                    const day = week[col] ?? week[0]!;
                    const all = events.filter((event) => overlapsDay(event, day)).sort((a, b) => Number(b.allDay) - Number(a.allDay) || a.start.getTime() - b.start.getTime());
                    return (
                      <div key={`more-${col}`} className="pointer-events-auto flex min-w-0 px-0.5" style={{ gridColumn: col + 1, gridRow: maxPerDay + 1 }}>
                        <Popover open={more === day.getTime()} onOpenChange={(open) => setMore(open ? day.getTime() : undefined)}>
                          <PopoverTrigger asChild>
                            <button type="button" className="rounded-sm px-1 text-xs text-muted-foreground outline-none hover:text-foreground hover:underline focus-visible:ring-2 focus-visible:ring-ring">
                              {moreLabel(list.length)}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="flex w-64 flex-col gap-1 p-2" aria-label={formatDayLabel(day)}>
                            <button type="button" onClick={() => onSelectDay(day)} className="mb-1 self-start rounded-sm text-sm font-semibold outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring">
                              {formatDayLabel(day)}
                            </button>
                            {all.map((event) => (
                              <div key={event.id}>{chip(event, undefined, true)}</div>
                            ))}
                          </PopoverContent>
                        </Popover>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
